import { ArrowTopRightOnSquareIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

interface SmtpPreset {
  host: string;
  port: number;
  encryption: 'tls' | 'ssl';
}

interface Provider {
  id: string;
  name: string;
  iconColor: string;
  iconLetter: string;
  preset: SmtpPreset;
  steps: string[];
  helpUrl: string;
  passwordNote: string;
}

const PROVIDERS: Provider[] = [
  {
    id: 'gmail',
    name: 'Gmail',
    iconColor: 'from-red-500 to-red-600',
    iconLetter: 'G',
    preset: { host: 'smtp.gmail.com', port: 587, encryption: 'tls' },
    steps: [
      'Connectez-vous à votre compte Google (myaccount.google.com)',
      'Allez dans "Sécurité" puis "Validation en deux étapes" et activez-la si ce n\'est pas déjà fait',
      'Revenez dans "Sécurité" et cliquez sur "Mots de passe des applications"',
      'Sélectionnez "Autre (nom personnalisé)" et entrez "Module Croissance Déclarations Juridiques"',
      'Cliquez sur "Générer" et copiez le mot de passe de 16 caractères',
      'Utilisez ce mot de passe d\'application dans le champ "Mot de passe" ci-dessus',
    ],
    helpUrl: 'https://support.google.com/accounts/answer/185833',
    passwordNote: 'Vous devez utiliser un "Mot de passe d\'application" et non votre mot de passe Google habituel.',
  },
  {
    id: 'outlook',
    name: 'Outlook / Office 365',
    iconColor: 'from-blue-500 to-blue-700',
    iconLetter: 'O',
    preset: { host: 'smtp.office365.com', port: 587, encryption: 'tls' },
    steps: [
      'Connectez-vous à votre compte Microsoft (account.microsoft.com)',
      'Allez dans "Sécurité" > "Options de sécurité avancées"',
      'Activez la vérification en deux étapes si ce n\'est pas déjà fait',
      'Faites défiler jusqu\'à "Mots de passe d\'application" et cliquez sur "Créer"',
      'Donnez un nom (ex: "Module Croissance Déclarations Juridiques") et notez le mot de passe généré',
      'Utilisez ce mot de passe d\'application dans le champ "Mot de passe" ci-dessus',
    ],
    helpUrl: 'https://support.microsoft.com/fr-fr/account-billing/g%C3%A9rer-les-mots-de-passe-d-application-pour-la-v%C3%A9rification-en-deux-%C3%A9tapes-d6dc8c6d-4bf7-4851-ad95-6d07799387e9',
    passwordNote: 'Si vous avez la vérification en 2 étapes activée, un mot de passe d\'application est requis.',
  },
  {
    id: 'yahoo',
    name: 'Yahoo Mail',
    iconColor: 'from-purple-500 to-purple-700',
    iconLetter: 'Y',
    preset: { host: 'smtp.mail.yahoo.com', port: 465, encryption: 'ssl' },
    steps: [
      'Connectez-vous à Yahoo Mail',
      'Cliquez sur votre avatar > "Informations de compte"',
      'Allez dans "Sécurité du compte"',
      'Activez la vérification en deux étapes',
      'Cliquez sur "Générer un mot de passe d\'application"',
      'Sélectionnez "Autre application" et entrez "Module Croissance Déclarations Juridiques"',
      'Copiez le mot de passe généré et utilisez-le ci-dessus',
    ],
    helpUrl: 'https://help.yahoo.com/kb/SLN15241.html',
    passwordNote: 'Un mot de passe d\'application est nécessaire si la vérification en 2 étapes est activée.',
  },
  {
    id: 'ovh',
    name: 'OVH',
    iconColor: 'from-cyan-600 to-blue-800',
    iconLetter: 'O',
    preset: { host: 'ssl0.ovh.net', port: 465, encryption: 'ssl' },
    steps: [
      'Connectez-vous à votre espace client OVH (ovh.com/manager)',
      'Allez dans "Web Cloud" > "Emails"',
      'Sélectionnez votre domaine et le compte email concerné',
      'Les paramètres SMTP sont visibles dans l\'onglet "Informations"',
      'Le serveur d\'envoi est "ssl0.ovh.net" avec le port 465 (SSL)',
      'Utilisez votre adresse email complète comme nom d\'utilisateur et votre mot de passe email OVH',
    ],
    helpUrl: 'https://docs.ovh.com/fr/emails/premiers-pas-avec-les-emails-mutualises/',
    passwordNote: 'Utilisez directement le mot de passe de votre compte email OVH.',
  },
  {
    id: 'ionos',
    name: 'IONOS (1&1)',
    iconColor: 'from-blue-700 to-indigo-800',
    iconLetter: 'I',
    preset: { host: 'smtp.ionos.fr', port: 587, encryption: 'tls' },
    steps: [
      'Connectez-vous à votre espace IONOS',
      'Allez dans "Email" > "Adresses email"',
      'Les paramètres SMTP sont : smtp.ionos.fr, port 587, TLS',
      'Votre nom d\'utilisateur est votre adresse email complète',
      'Utilisez le mot de passe de votre boîte mail IONOS',
    ],
    helpUrl: 'https://www.ionos.fr/assistance/email/parametres-des-serveurs-de-messagerie/',
    passwordNote: 'Utilisez votre mot de passe de messagerie IONOS.',
  },
];

interface SmtpTutorialProps {
  onApplyPreset: (preset: SmtpPreset) => void;
}

export default function SmtpTutorial({ onApplyPreset }: SmtpTutorialProps) {
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);

  const provider = PROVIDERS.find(p => p.id === selectedProvider);

  return (
    <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="p-5">
        <h5 className="mb-1 text-sm font-bold text-gray-900">Quel fournisseur de messagerie utilisez-vous ?</h5>
        <p className="mb-4 text-xs text-gray-500">Sélectionnez votre fournisseur pour pré-remplir les paramètres et obtenir un guide pas-à-pas.</p>

        <div className="flex flex-wrap gap-2">
          {PROVIDERS.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedProvider(selectedProvider === p.id ? null : p.id)}
              className={`flex items-center gap-2 rounded-lg border-2 px-4 py-2.5 text-sm font-medium transition-all ${
                selectedProvider === p.id
                  ? 'border-blue-500 bg-white shadow-md'
                  : 'border-transparent bg-white/70 hover:border-gray-300 hover:bg-white'
              }`}
            >
              <div className={`flex h-7 w-7 items-center justify-center rounded bg-gradient-to-br ${p.iconColor} text-xs font-bold text-white`}>
                {p.iconLetter}
              </div>
              {p.name}
            </button>
          ))}
        </div>
      </div>

      {provider && (
        <div className="border-t border-blue-200 p-5">
          <div className="mb-4 flex items-center justify-between">
            <h6 className="font-semibold text-gray-900">
              Configuration {provider.name}
            </h6>
            <button
              type="button"
              onClick={() => onApplyPreset(provider.preset)}
              className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              <CheckIcon className="h-4 w-4" />
              Appliquer ces paramètres
            </button>
          </div>

          <div className="mb-4 grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-white p-3 shadow-sm">
              <span className="text-xs font-medium text-gray-500">Serveur</span>
              <p className="font-mono text-sm font-semibold text-gray-900">{provider.preset.host}</p>
            </div>
            <div className="rounded-lg bg-white p-3 shadow-sm">
              <span className="text-xs font-medium text-gray-500">Port</span>
              <p className="font-mono text-sm font-semibold text-gray-900">{provider.preset.port}</p>
            </div>
            <div className="rounded-lg bg-white p-3 shadow-sm">
              <span className="text-xs font-medium text-gray-500">Chiffrement</span>
              <p className="text-sm font-semibold text-gray-900 uppercase">{provider.preset.encryption}</p>
            </div>
          </div>

          {provider.passwordNote && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-800">
                <ExclamationTriangleIcon className="mr-1 mb-0.5 inline h-4 w-4" />
                {provider.passwordNote}
              </p>
            </div>
          )}

          <div className="rounded-lg bg-white p-4 shadow-sm">
            <h6 className="mb-3 text-sm font-semibold text-gray-800">Guide pas-à-pas</h6>
            <ol className="space-y-2.5">
              {provider.steps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                    {i + 1}
                  </span>
                  <span className="text-sm leading-relaxed text-gray-700">{step}</span>
                </li>
              ))}
            </ol>

            <a
              href={provider.helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              Documentation officielle
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

